import React, {
  Suspense,
  lazy,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import {
  getPremiumLandingPath,
  subscriptionRouteRedirectTarget,
} from './utils/subscriptionProductScope';
import {
  isLearnerAppBlockedUser,
  redirectToStaffConsole,
} from './utils/staffAppAccess';
import {
  clearAllCaches,
  consumeStoredPostLoginRedirect,
  GOOGLE_CLIENT_ID,
  isTokenValid,
  redirectToLoginWithReturn,
} from './utils/utils';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {
  AUTH_EVENT_TYPES,
  getAuthTabId,
  getStoredUser,
  bootstrapAuthSession,
  resolveAuthTabLifecycleOnBoot,
  registerAuthTabLifecycle,
  refreshSessionIfNeeded,
  subscribeToAuthEvents,
} from './utils/authSession';
import './core/Core.css';
import Signup from './authentification/Signup';
import Login from './authentification/Login';
import ForgotPassword from './authentification/ForgotPassword';
import ResetPassword from './authentification/ResetPassword';
import Terms from './authentification/Terms';
import PaymentSuccess from './pricing/PaymentSuccess';
import Coaching from './core/coaching/Coaching';
import CoachingPaymentSuccess from './core/coaching/CoachingPaymentSuccess';
import Resources from './core/resources/Resources';
import LearningDirectory from './core/resources/LearningDirectory';
import Settings from './core/settings/Settings';
import Webinar from './core/webinars/Webinar';
import ApplicationPackage from './core/application/ApplicationPackage';
import DocumentReviewQueue from './core/coaching/DocumentReviewQueue';
import Funding from './core/funding/Funding';
import PrepCopilot from './core/copilot/PrepCopilot';
import Interview from './core/interviews/Interview';
import English from './core/exams/English';
import Results from './core/exams/english/Results';
import ReadingTips from './core/exams/english/ReadingTips';
import ListeningTips from './core/exams/english/ListeningTips';
import SpeakingTips from './core/exams/english/SpeakingTips';
import WritingTips from './core/exams/english/WritingTips';
import MockExamBreak from './core/exams/english/MockExamBreak';
import Progress from './core/exams/english/Progress';
//vocabularytips
import TopicRelatedVocabularyTips from './core/exams/english/vocabulary/TopicRelatedVocabularyTips';
import EssentialWordsTips from './core/exams/english/vocabulary/EssentialWordsTips';
import IdiomsTips from './core/exams/english/vocabulary/IdiomsTips';
import PhrasalVerbsTips from './core/exams/english/vocabulary/PhrasalVerbsTips';
import CollocationTips from './core/exams/english/vocabulary/CollocationTips';
import ParaphrasingTips from './core/exams/english/vocabulary/ParaphrasingTips';
import UncountableNounsTips from './core/exams/english/vocabulary/UncountableNounsTips';
import IndividualWordsTips from './core/exams/english/vocabulary/IndividualWordsTips';
import SpellingUSUKTips from './core/exams/english/vocabulary/SpellingUSUKTips';
import VocabularyForWriting from './core/exams/english/vocabulary/VocabularyForWriting';

//grammartips
import SentenceClauseTips from './core/exams/english/grammar/SentenceClauseTips';
import ComplexSentencesTips from './core/exams/english/grammar/ComplexSentencesTips';
import AdverbialClauseTips from './core/exams/english/grammar/AdverbialClauseTips';
import RelativeClauseTips from './core/exams/english/grammar/RelativeClauseTips';
import SentenceStructureTips from './core/exams/english/grammar/SentenceStructureTips';
import NounClauseTips from './core/exams/english/grammar/NounClauseTips';
import GrammarRulesTips from './core/exams/english/grammar/GrammarRulesTips';
import ModalVerbsTips from './core/exams/english/grammar/ModalVerbsTips';
import SubjectVerbAgreementTips from './core/exams/english/grammar/SubjectVerbAgreementTips';
import GerundsTips from './core/exams/english/grammar/GerundsTips';
import InfinitiveTips from './core/exams/english/grammar/InfinitiveTips';
import GerundsVsInfinitivesTips from './core/exams/english/grammar/GerundsVsInfinitivesTips';
import ParallelStructureTips from './core/exams/english/grammar/ParallelStructureTips';
import PassiveVoiceTips from './core/exams/english/grammar/PassiveVoiceTips';

// IELTS Skills Courses (from english/courses folder)
import ListeningStrategies from './core/exams/english/courses/ListeningStrategies';
import NoteTakingSkills from './core/exams/english/courses/NoteTakingSkills';
import ListeningForDetails from './core/exams/english/courses/ListeningForDetails';
import AccentTraining from './core/exams/english/courses/AccentTraining';
import SkimmingScanning from './core/exams/english/courses/SkimmingScanning';
import ReadingStrategies from './core/exams/english/courses/ReadingStrategies';
import ReadingTimeManagement from './core/exams/english/courses/ReadingTimeManagement';
import AcademicVocabulary from './core/exams/english/courses/AcademicVocabulary';
import Task1Overview from './core/exams/english/courses/Task1Overview';
import EssayStructure from './core/exams/english/courses/EssayStructure';
import CoherenceCohesion from './core/exams/english/courses/CoherenceCohesion';
import BandDescriptors from './core/exams/english/courses/BandDescriptors';
import FluencyTraining from './core/exams/english/courses/FluencyTraining';
import PronunciationCourse from './core/exams/english/courses/PronunciationCourse';
import SpeakingPart2 from './core/exams/english/courses/SpeakingPart2';
import SpeakingPart3 from './core/exams/english/courses/SpeakingPart3';
import DynamicLesson from './core/exams/english/courses/DynamicLesson';

//gre
import VocabularyArticle from './core/exams/gre/VocabularyArticle';
import ArticleHistory from './core/exams/gre/ArticleHistory';
import FlashcardBook from './core/exams/gre/FlashcardBook';
import ReadingComprehension from './core/exams/gre/ReadingComprehension';
import SentenceEquivalence from './core/exams/gre/SentenceEquivalence';
import VerbalProgress from './core/exams/gre/VerbalProgress';
import QuestionTypesLesson from './core/exams/gre/reading-lessons/QuestionTypesLesson';
import FiveStepProcessLesson from './core/exams/gre/reading-lessons/FiveStepProcessLesson';
import PassageComponentsLesson from './core/exams/gre/reading-lessons/PassageComponentsLesson';
import ChunkReadingLesson from './core/exams/gre/reading-lessons/ChunkReadingLesson';
import SpeedReadingLesson from './core/exams/gre/reading-lessons/SpeedReadingLesson';
import TrapAnswersLesson from './core/exams/gre/reading-lessons/TrapAnswersLesson';
import MasterPassageLesson from './core/exams/gre/reading-lessons/MasterPassageLesson';
import ReadingComprehensionTest from './core/exams/gre/ReadingComprehensionTest';
import ArithmeticFoundationsLesson from './core/exams/gre/quant-lessons/ArithmeticFoundationsLesson';
import MathTranslationLesson from './core/exams/gre/quant-lessons/MathTranslationLesson';
import IntegersLesson from './core/exams/gre/quant-lessons/IntegersLesson';
import PercentageRatioLesson from './core/exams/gre/quant-lessons/PercentageRatioLesson';
import AlgebraLesson from './core/exams/gre/quant-lessons/AlgebraLesson';
import AbsoluteValueLesson from './core/exams/gre/quant-lessons/AbsoluteValueLesson';
import ProbabilitiesLesson from './core/exams/gre/quant-lessons/ProbabilitiesLesson';
import WorkRateLesson from './core/exams/gre/quant-lessons/WorkRateLesson';
import DistanceRateLesson from './core/exams/gre/quant-lessons/DistanceRateLesson';
import CoordinateGeometryLesson from './core/exams/gre/quant-lessons/CoordinateGeometryLesson';
import CirclesLesson from './core/exams/gre/quant-lessons/CirclesLesson';
import TrianglesLesson from './core/exams/gre/quant-lessons/TrianglesLesson';
import QuadrilateralsLesson from './core/exams/gre/quant-lessons/QuadrilateralsLesson';
import PolygonsLesson from './core/exams/gre/quant-lessons/PolygonsLesson';
import ThreeDimensionalFiguresLesson from './core/exams/gre/quant-lessons/ThreeDimensionalFiguresLesson';
import StatisticsLesson from './core/exams/gre/quant-lessons/StatisticsLesson';
import VolumesLesson from './core/exams/gre/quant-lessons/VolumesLesson';
import GraphsLesson from './core/exams/gre/quant-lessons/GraphsLesson';
import ExponentsLesson from './core/exams/gre/quant-lessons/ExponentsLesson';
import SetsLesson from './core/exams/gre/quant-lessons/SetsLesson';
import MathShortcutsLesson from './core/exams/gre/quant-lessons/MathShortcutsLesson';
import FunctionsLesson from './core/exams/gre/quant-lessons/FunctionsLesson';
import SequencesLesson from './core/exams/gre/quant-lessons/SequencesLesson';
import SavedConcepts from './core/exams/gre/quant-lessons/SavedConcepts';
import QuantStrategy from './core/exams/gre/QuantStrategy';
import GREVocabularyLesson from './core/exams/gre/verbal-lessons/GREVocabularyLesson';
import QuantProgress from './core/exams/gre/QuantProgress';
import QuantPracticeSession from './core/exams/gre/QuantPracticeSession';
import Home from './core/home/Home';
import TAGELesson from './core/exams/tage/TAGELesson';
import TAGESchoolSelection from './core/exams/tage/TAGESchoolSelection';
import TAGEStudyPlans from './core/exams/tage/TAGEStudyPlans';
import TAGENotebook from './core/exams/tage/TAGENotebook';
import TAGECoaching from './core/coaching/tage/TAGECoaching';

// ============================================
// LAZY LOADED COMPONENTS (Code Splitting)
// ============================================

// Large components (>100KB) - lazy loaded for better performance
const Pricing = lazy(() => import('./pricing/Pricing'));
const Community = lazy(() => import('./core/community/Community'));
const CoachProfile = lazy(() => import('./core/coaching/CoachProfile'));
const GRE = lazy(() => import('./core/exams/GRE'));
const TAGE = lazy(() => import('./core/exams/TAGE'));
const VocabularyMastery = lazy(
  () => import('./core/exams/gre/VocabularyMastery')
);
const TextCompletion = lazy(() => import('./core/exams/gre/TextCompletion'));

// Heavy English exam components
const Listening = lazy(() => import('./core/exams/english/Listening'));
const ListeningReview = lazy(
  () => import('./core/exams/english/ListeningReview')
);
const Reading = lazy(() => import('./core/exams/english/Reading'));
const Writing = lazy(() => import('./core/exams/english/Writing'));
const Speaking = lazy(() => import('./core/exams/english/Speaking'));
const SpeakingResults = lazy(
  () => import('./core/exams/english/SpeakingResults')
);
const LanguageJourney = lazy(
  () => import('./core/exams/english/LanguageJourney')
);
const RevisionSession = lazy(
  () => import('./core/exams/english/RevisionSession')
);
const WeeklyReview = lazy(
  () => import('./core/exams/english/WeeklyReview')
);
const SpecialistBooking = lazy(
  () => import('./core/exams/english/SpecialistBooking')
);

// Heavy GRE test components
const GREAWATest = lazy(() => import('./core/exams/gre/GREAWATest'));
const GREQuantTest = lazy(() => import('./core/exams/gre/GREQuantTest'));
const GREVerbalTest = lazy(() => import('./core/exams/gre/GREVerbalTest'));
const GREFullTest = lazy(() => import('./core/exams/gre/GREFullTest'));

// Heavy TAGE components
const TAGECDTArticleTest = lazy(
  () => import('./core/exams/tage/TAGECDTArticleTest')
);
const TAGEDiagnosticTest = lazy(
  () => import('./core/exams/tage/TAGEDiagnosticTest')
);
const TAGEFullTest = lazy(() => import('./core/exams/tage/TAGEFullTest'));
const ToeflEntry = lazy(() => import('./core/exams/toefl/ToeflEntry'));
const ToeflHub = lazy(() => import('./core/exams/toefl/Hub'));
const ToeflActivation = lazy(() => import('./core/exams/toefl/Activation'));
const ToeflDiagnostic = lazy(() => import('./core/exams/toefl/Diagnostic'));
const ToeflReading = lazy(() => import('./core/exams/toefl/Reading'));
const ToeflListening = lazy(() => import('./core/exams/toefl/Listening'));
const ToeflSpeaking = lazy(() => import('./core/exams/toefl/Speaking'));
const ToeflWriting = lazy(() => import('./core/exams/toefl/Writing'));
const ToeflMockSession = lazy(() => import('./core/exams/toefl/MockSession'));
const ToeflCourseRoute = lazy(() => import('./core/exams/toefl/courses/ToeflCourseRoute'));
const ToeflResults = lazy(() => import('./core/exams/toefl/Results'));
const ToeflBoosterRedirect = lazy(() => import('./core/exams/toefl/BoosterRedirect'));
const AdminUserControls = lazy(() => import('./core/admin/AdminUserControls'));
const AdminWebinars = lazy(() => import('./core/admin/AdminWebinars'));
const PublicWebinarRegistration = lazy(
  () => import('./core/webinars/PublicWebinarRegistration')
);
const CopilotVisualPreview =
  process.env.NODE_ENV === 'development'
    ? lazy(() => import('./core/copilot/CopilotVisualPreview'))
    : null;

const resolveAuthenticatedLandingPath = (user) => {
  if (!user) return '/login';
  if (isLearnerAppBlockedUser(user)) return null;
  if (user.role?.name === 'coach') return '/coaching';
  return user.is_premium ? getPremiumLandingPath(user) : '/community';
};

const rejectStaffOnlyAppSession = () => {
  clearAllCaches();
  redirectToStaffConsole();
};

const StaffOnlyAppRedirect = () => {
  useEffect(() => {
    rejectStaffOnlyAppSession();
  }, []);
  return <LoadingFallback />;
};

// Premium Route Protection Component
const PremiumRoute = ({ children }) => {
  const user = getStoredUser();
  const location = useLocation();
  const hasValidSession = Boolean(user) && isTokenValid();

  // If user is not logged in at all, redirect to login with return URL
  if (!hasValidSession) {
    const currentPath = location.pathname + location.search;
    return (
      <Navigate
        to="/login"
        replace
        state={{
          redirectTo: currentPath,
          message: user
            ? 'Votre session a expiré. Veuillez vous reconnecter.'
            : 'Veuillez vous connecter pour accéder à ce contenu.',
        }}
      />
    );
  }

  if (isLearnerAppBlockedUser(user)) {
    return <StaffOnlyAppRedirect />;
  }

  // If user is logged in but not premium and not a coach, redirect to community
  if (!user.is_premium && user.role?.name !== 'coach') {
    return <Navigate to="/community" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const user = getStoredUser();
  const location = useLocation();
  const hasValidSession = Boolean(user) && isTokenValid();

  if (!hasValidSession) {
    const currentPath = location.pathname + location.search;
    return (
      <Navigate
        to="/login"
        replace
        state={{
          redirectTo: currentPath,
          message: user
            ? 'Votre session a expiré. Veuillez vous reconnecter.'
            : 'Veuillez vous connecter pour accéder à ce contenu.',
        }}
      />
    );
  }

  if (user.role?.name !== 'admin') {
    return (
      <Navigate
        to={user.is_premium ? getPremiumLandingPath(user) : '/community'}
        replace
      />
    );
  }

  return children;
};

const LearnerAppSessionGuard = ({ children }) => {
  const user = getStoredUser();
  const hasValidSession = Boolean(user) && isTokenValid();

  if (hasValidSession && isLearnerAppBlockedUser(user)) {
    return <StaffOnlyAppRedirect />;
  }

  return children;
};

const PublicAuthRoute = ({ children }) => {
  const user = getStoredUser();
  const hasValidSession = Boolean(user) && isTokenValid();

  if (hasValidSession) {
    if (isLearnerAppBlockedUser(user)) {
      return <StaffOnlyAppRedirect />;
    }
    // If the user landed on /login because of an unauthorized deep link
    // (e.g. emailed article URL), honor that intent before falling back to
    // the generic landing page. Without this, a session restored via the
    // refresh cookie would yank the user to the dashboard and lose the link.
    const pendingRedirect = consumeStoredPostLoginRedirect();
    if (pendingRedirect) {
      return <Navigate to={pendingRedirect} replace />;
    }
    const landingPath = resolveAuthenticatedLandingPath(user);
    if (!landingPath) {
      return <StaffOnlyAppRedirect />;
    }
    return <Navigate to={landingPath} replace />;
  }

  return children;
};

// Note: /calendar and /onboarding routes now render Pricing directly
// The Pricing component detects the route and loads the appropriate service

// Loading component for Suspense fallback - uses Core.css styles for consistency
const LoadingFallback = () => (
  <div className="layout-loading" style={{ minHeight: '100vh' }}>
    <div className="loading-spinner"></div>
    <p>Loading...</p>
  </div>
);

// Prevent "flash at previous scroll position" on route changes.
const ScrollToTopOnRouteChange = () => {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    // If a route uses an anchor, let the browser handle it.
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search, hash]);

  return null;
};

const ProductScopeNavigationGuard = () => {
  const location = useLocation();
  const user = getStoredUser();
  const redirectTarget = subscriptionRouteRedirectTarget(
    location.pathname,
    user
  );

  if (redirectTarget && redirectTarget !== location.pathname) {
    if (redirectTarget === '/login') {
      const currentPath = location.pathname + location.search;
      return (
        <Navigate
          to="/login"
          replace
          state={{
            redirectTo: currentPath,
            message: 'Veuillez vous connecter pour accéder à ce contenu.',
          }}
        />
      );
    }
    return <Navigate to={redirectTarget} replace />;
  }

  return null;
};

const AppShell = () => {
  const [isAuthBootstrapReady, setIsAuthBootstrapReady] = useState(false);
  const [, setAuthRenderVersion] = useState(0);

  useEffect(() => {
    let cleanupLifecycle = () => {};
    let isMounted = true;

    const initAuth = async () => {
      resolveAuthTabLifecycleOnBoot();
      cleanupLifecycle = registerAuthTabLifecycle();
      await bootstrapAuthSession();
      if (isMounted) {
        setIsAuthBootstrapReady(true);
      }
    };

    initAuth();

    return () => {
      isMounted = false;
      cleanupLifecycle();
    };
  }, []);

  useEffect(() => {
    const currentTabId = getAuthTabId();
    let isCancelled = false;

    const rehydrateFromSharedSession = async () => {
      const refreshResult = await refreshSessionIfNeeded({ force: true });

      if (isCancelled || !refreshResult?.ok) {
        return;
      }

      setIsAuthBootstrapReady(true);
      setAuthRenderVersion((version) => version + 1);
    };

    const unsubscribe = subscribeToAuthEvents((authEvent) => {
      if (!authEvent?.type || authEvent.sourceTabId === currentTabId) {
        return;
      }

      if (authEvent.type === AUTH_EVENT_TYPES.logout) {
        setAuthRenderVersion((version) => version + 1);
        if (window.location.pathname !== '/login') {
          redirectToLoginWithReturn({
            message:
              authEvent.payload?.reason === 'session_expired'
                ? 'Votre session a expire. Veuillez vous reconnecter.'
                : 'Veuillez vous connecter pour continuer.',
            clearCachesFirst: true,
          });
        } else {
          clearAllCaches();
        }
        return;
      }

      if (
        authEvent.type === AUTH_EVENT_TYPES.login ||
        authEvent.type === AUTH_EVENT_TYPES.sessionRefreshed
      ) {
        rehydrateFromSharedSession();
      }
    });

    return () => {
      isCancelled = true;
      unsubscribe();
    };
  }, []);

  if (!isAuthBootstrapReady) {
    return <LoadingFallback />;
  }

  return (
    <>
      <ScrollToTopOnRouteChange />
      <ProductScopeNavigationGuard />
      <LearnerAppSessionGuard>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route
            path="/signup"
            element={
              <PublicAuthRoute>
                <Signup />
              </PublicAuthRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicAuthRoute>
                <Login />
              </PublicAuthRoute>
            }
          />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/resetpassword" element={<ResetPassword />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/pricing/booking" element={<Pricing />} />
          <Route path="/calendar" element={<Pricing />} />
          <Route path="/onboarding" element={<Pricing />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route
            path="/coaching/payment-success"
            element={<CoachingPaymentSuccess />}
          />
          <Route
            path="/coaching/booking-success"
            element={<CoachingPaymentSuccess />}
          />
          <Route
            path="/home"
            element={
              <PremiumRoute>
                <Home />
              </PremiumRoute>
            }
          />
          <Route path="/community" element={<Community />} />
          <Route path="/community/posts/:postId" element={<Community />} />
          <Route path="/coaching" element={<Coaching />} />
          <Route path="/coach/:coachId" element={<CoachProfile />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/:resourceId" element={<Resources />} />
          <Route path="/directory" element={<LearningDirectory />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/webinars" element={<Webinar />} />
          <Route
            path="/webinars/join/:slug"
            element={<PublicWebinarRegistration />}
          />
          <Route path="/terms" element={<Terms />} />
          {process.env.NODE_ENV === 'development' && CopilotVisualPreview && (
            <Route
              path="/dev/liwaza-ai"
              element={<CopilotVisualPreview />}
            />
          )}
          <Route
            path="/profilebuilder"
            element={<Navigate to="/applications" replace />}
          />
          <Route
            path="/mbaapplication"
            element={<Navigate to="/applications" replace />}
          />
          <Route
            path="/applications"
            element={
              <PremiumRoute>
                <ApplicationPackage />
              </PremiumRoute>
            }
          />
          <Route
            path="/coach/document-reviews"
            element={
              <PremiumRoute>
                <DocumentReviewQueue />
              </PremiumRoute>
            }
          />
          <Route
            path="/coaching/document-reviews"
            element={<Navigate to="/coach/document-reviews" replace />}
          />
          <Route
            path="/coach/reviews"
            element={<Navigate to="/coach/document-reviews" replace />}
          />
          <Route
            path="/coach/funding-reviews"
            element={
              <PremiumRoute>
                <DocumentReviewQueue kind="funding" />
              </PremiumRoute>
            }
          />
          <Route
            path="/funding"
            element={
              <PremiumRoute>
                <Funding />
              </PremiumRoute>
            }
          />
          <Route
            path="/prepcopilot"
            element={
              <PremiumRoute>
                <PrepCopilot />
              </PremiumRoute>
            }
          />
          <Route
            path="/essays"
            element={<Navigate to="/applications" replace />}
          />
          <Route
            path="/exams/gre"
            element={
              <PremiumRoute>
                <GRE />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/tage"
            element={
              <PremiumRoute>
                <TAGE />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/tage/lesson/:lessonId"
            element={
              <PremiumRoute>
                <TAGELesson />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/tage/cdt-article/:lessonId"
            element={
              <PremiumRoute>
                <TAGECDTArticleTest />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/tage/schools"
            element={
              <PremiumRoute>
                <TAGESchoolSelection />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/tage/diagnostic"
            element={
              <PremiumRoute>
                <TAGEDiagnosticTest />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/tage/full-test"
            element={
              <PremiumRoute>
                <TAGEFullTest />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/tage/study-plans"
            element={
              <PremiumRoute>
                <TAGEStudyPlans />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/tage/notebook"
            element={
              <PremiumRoute>
                <TAGENotebook />
              </PremiumRoute>
            }
          />
          <Route
            path="/coaching/tage"
            element={
              <PremiumRoute>
                <TAGECoaching />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/english"
            element={
              <PremiumRoute>
                <English />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/english/revision-session"
            element={<RevisionSession />}
          />
          <Route
            path="/exams/english/lesson/:courseId"
            element={<DynamicLesson />}
          />
          <Route path="/exams/english/listening" element={<Listening />} />
          <Route
            path="/exams/english/listening-review"
            element={<ListeningReview />}
          />
          <Route path="/exams/english/reading" element={<Reading />} />
          <Route path="/exams/english/speaking" element={<Speaking />} />
          <Route path="/exams/english/writing" element={<Writing />} />
          <Route path="/exams/english/reading-tips" element={<ReadingTips />} />
          <Route
            path="/exams/english/listening-tips"
            element={<ListeningTips />}
          />
          <Route
            path="/exams/english/speaking-tips"
            element={<SpeakingTips />}
          />
          <Route path="/exams/english/writing-tips" element={<WritingTips />} />
          <Route
            path="/exams/english/vocabulary/topic-related"
            element={<TopicRelatedVocabularyTips />}
          />
          <Route
            path="/exams/english/vocabulary/essential-words"
            element={<EssentialWordsTips />}
          />
          <Route
            path="/exams/english/vocabulary/idioms"
            element={<IdiomsTips />}
          />
          <Route
            path="/exams/english/vocabulary/phrasal-verbs"
            element={<PhrasalVerbsTips />}
          />
          <Route
            path="/exams/english/vocabulary/collocations"
            element={<CollocationTips />}
          />
          <Route
            path="/exams/english/vocabulary/paraphrasing"
            element={<ParaphrasingTips />}
          />
          <Route
            path="/exams/english/vocabulary/uncountable-nouns"
            element={<UncountableNounsTips />}
          />
          <Route
            path="/exams/english/vocabulary/individual-words"
            element={<IndividualWordsTips />}
          />
          <Route
            path="/exams/english/vocabulary/spelling-us-uk"
            element={<SpellingUSUKTips />}
          />
          <Route
            path="/exams/english/vocabulary/vocabulary-for-writing"
            element={<VocabularyForWriting />}
          />
          <Route
            path="/exams/english/grammar/sentence-clauses"
            element={<SentenceClauseTips />}
          />
          <Route
            path="/exams/english/grammar/adverbial-clauses"
            element={<AdverbialClauseTips />}
          />
          <Route
            path="/exams/english/grammar/relative-clauses"
            element={<RelativeClauseTips />}
          />
          <Route
            path="/exams/english/grammar/complex-sentences"
            element={<ComplexSentencesTips />}
          />
          <Route
            path="/exams/english/grammar/sentence-structure"
            element={<SentenceStructureTips />}
          />
          <Route
            path="/exams/english/grammar/noun-clauses"
            element={<NounClauseTips />}
          />
          <Route
            path="/exams/english/grammar/grammar-rules"
            element={<GrammarRulesTips />}
          />
          <Route
            path="/exams/english/grammar/modal-verbs"
            element={<ModalVerbsTips />}
          />
          <Route
            path="/exams/english/grammar/subject-verb-agreement"
            element={<SubjectVerbAgreementTips />}
          />
          <Route
            path="/exams/english/grammar/gerunds"
            element={<GerundsTips />}
          />
          <Route
            path="/exams/english/grammar/infinitives"
            element={<InfinitiveTips />}
          />
          <Route
            path="/exams/english/grammar/gerund-infinitive"
            element={<GerundsVsInfinitivesTips />}
          />
          <Route
            path="/exams/english/grammar/parallel-structure"
            element={<ParallelStructureTips />}
          />
          <Route
            path="/exams/english/grammar/passive-voice"
            element={<PassiveVoiceTips />}
          />
          <Route
            path="/exams/english/courses/listening-strategies"
            element={<ListeningStrategies />}
          />
          <Route
            path="/exams/english/courses/note-taking"
            element={<NoteTakingSkills />}
          />
          <Route
            path="/exams/english/courses/listening-details"
            element={<ListeningForDetails />}
          />
          <Route
            path="/exams/english/courses/accent-training"
            element={<AccentTraining />}
          />
          <Route
            path="/exams/english/courses/skimming-scanning"
            element={<SkimmingScanning />}
          />
          <Route
            path="/exams/english/courses/reading-strategies"
            element={<ReadingStrategies />}
          />
          <Route
            path="/exams/english/courses/reading-time"
            element={<ReadingTimeManagement />}
          />
          <Route
            path="/exams/english/courses/academic-vocab"
            element={<AcademicVocabulary />}
          />
          <Route
            path="/exams/english/courses/task1-overview"
            element={<Task1Overview />}
          />
          <Route
            path="/exams/english/courses/essay-structure"
            element={<EssayStructure />}
          />
          <Route
            path="/exams/english/courses/coherence-cohesion"
            element={<CoherenceCohesion />}
          />
          <Route
            path="/exams/english/courses/band-descriptors"
            element={<BandDescriptors />}
          />
          <Route
            path="/exams/english/courses/fluency"
            element={<FluencyTraining />}
          />
          <Route
            path="/exams/english/courses/pronunciation"
            element={<PronunciationCourse />}
          />
          <Route
            path="/exams/english/courses/speaking-part2"
            element={<SpeakingPart2 />}
          />
          <Route
            path="/exams/english/courses/speaking-part3"
            element={<SpeakingPart3 />}
          />
          <Route
            path="/interview"
            element={
              <PremiumRoute>
                <Interview />
              </PremiumRoute>
            }
          />
          <Route
            path="/cv"
            element={<Navigate to="/applications?kind=cv" replace />}
          />
          <Route
            path="/exams/english/mock-exam-break"
            element={<MockExamBreak />}
          />
          <Route
            path="/exams/english/:testType/results/:testId"
            element={<Results />}
          />
          <Route
            path="/exams/english/speaking-results/:testId"
            element={<SpeakingResults />}
          />
          <Route
            path="/exams/english/progression"
            element={<Progress sidebar={true} />}
          />
          <Route
            path="/exams/english/journey"
            element={
              <PremiumRoute>
                <LanguageJourney />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/english/weekly-review"
            element={
              <PremiumRoute>
                <WeeklyReview />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl"
            element={
              <PremiumRoute>
                <ToeflEntry />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/hub"
            element={
              <PremiumRoute>
                <ToeflHub />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/activate"
            element={
              <PremiumRoute>
                <ToeflActivation />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/diagnostic"
            element={
              <PremiumRoute>
                <ToeflDiagnostic />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/reading"
            element={
              <PremiumRoute>
                <ToeflReading />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/listening"
            element={
              <PremiumRoute>
                <ToeflListening />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/speaking"
            element={
              <PremiumRoute>
                <ToeflSpeaking />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/writing"
            element={
              <PremiumRoute>
                <ToeflWriting />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/mock/:sessionId"
            element={
              <PremiumRoute>
                <ToeflMockSession />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/courses/:slug"
            element={
              <PremiumRoute>
                <ToeflCourseRoute />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/booster/:slug"
            element={
              <PremiumRoute>
                <ToeflBoosterRedirect />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/toefl/results"
            element={
              <PremiumRoute>
                <ToeflResults />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/english/book-pamela"
            element={
              <PremiumRoute>
                <SpecialistBooking />
              </PremiumRoute>
            }
          />
          <Route
            path="/admin/user-controls"
            element={
              <AdminRoute>
                <AdminUserControls />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/webinars"
            element={
              <AdminRoute>
                <AdminWebinars />
              </AdminRoute>
            }
          />
          <Route
            path="/exams/gre/verbal/progress"
            element={<VerbalProgress />}
          />
          <Route
            path="/exams/gre/verbal/vocabulary"
            element={<VocabularyMastery />}
          />
          <Route path="/articles/enroll" element={<VocabularyMastery />} />
          <Route
            path="/exams/gre/verbal/vocabulary/dashboard"
            element={<VocabularyArticle />}
          />
          <Route
            path="/exams/gre/verbal/vocabulary/history"
            element={<ArticleHistory />}
          />
          <Route
            path="/exams/gre/verbal/vocabulary/flashcards"
            element={<FlashcardBook />}
          />
          <Route
            path="/exams/gre/reading-comprehension"
            element={<ReadingComprehension />}
          />
          <Route
            path="/exams/gre/reading-comprehension/lesson/question-types"
            element={<QuestionTypesLesson />}
          />
          <Route
            path="/exams/gre/reading-comprehension/lesson/five-step-process"
            element={<FiveStepProcessLesson />}
          />
          <Route
            path="/exams/gre/reading-comprehension/lesson/passage-components"
            element={<PassageComponentsLesson />}
          />
          <Route
            path="/exams/gre/reading-comprehension/lesson/chunk-reading"
            element={<ChunkReadingLesson />}
          />
          <Route
            path="/exams/gre/reading-comprehension/lesson/speed-reading"
            element={<SpeedReadingLesson />}
          />
          <Route
            path="/exams/gre/reading-comprehension/lesson/trap-answers"
            element={<TrapAnswersLesson />}
          />
          <Route
            path="/exams/gre/reading-comprehension/lesson/master-passage"
            element={<MasterPassageLesson />}
          />
          <Route
            path="/exams/gre/reading-comprehension/:testId"
            element={<ReadingComprehensionTest />}
          />
          <Route
            path="/exams/gre/verbal/vocabulary/text-completion"
            element={<TextCompletion />}
          />
          <Route
            path="/exams/gre/verbal/vocabulary/sentence-equivalence"
            element={<SentenceEquivalence />}
          />
          <Route
            path="/exams/gre/concepts/arithmetic-foundations"
            element={<ArithmeticFoundationsLesson />}
          />
          <Route
            path="/exams/gre/concepts/math-translation"
            element={<MathTranslationLesson />}
          />
          <Route
            path="/exams/gre/concepts/math-shortcuts"
            element={<MathShortcutsLesson />}
          />
          <Route
            path="/exams/gre/concepts/integers"
            element={<IntegersLesson />}
          />
          <Route
            path="/exams/gre/concepts/percentage-ratio"
            element={<PercentageRatioLesson />}
          />
          <Route
            path="/exams/gre/concepts/algebra"
            element={<AlgebraLesson />}
          />
          <Route
            path="/exams/gre/concepts/absolute-value"
            element={<AbsoluteValueLesson />}
          />
          <Route
            path="/exams/gre/concepts/probabilities"
            element={<ProbabilitiesLesson />}
          />
          <Route
            path="/exams/gre/concepts/work-rate"
            element={<WorkRateLesson />}
          />
          <Route
            path="/exams/gre/concepts/distance-rate"
            element={<DistanceRateLesson />}
          />
          <Route
            path="/exams/gre/concepts/coordinate-geometry"
            element={<CoordinateGeometryLesson />}
          />
          <Route
            path="/exams/gre/concepts/circles"
            element={<CirclesLesson />}
          />
          <Route
            path="/exams/gre/concepts/triangles"
            element={<TrianglesLesson />}
          />
          <Route
            path="/exams/gre/concepts/quadrilaterals"
            element={<QuadrilateralsLesson />}
          />
          <Route
            path="/exams/gre/concepts/polygons"
            element={<PolygonsLesson />}
          />
          <Route
            path="/exams/gre/concepts/three-dimensional-figures"
            element={<ThreeDimensionalFiguresLesson />}
          />
          <Route
            path="/exams/gre/concepts/statistics"
            element={<StatisticsLesson />}
          />
          <Route
            path="/exams/gre/concepts/volumes"
            element={<VolumesLesson />}
          />
          <Route path="/exams/gre/concepts/graphs" element={<GraphsLesson />} />
          <Route
            path="/exams/gre/concepts/exponents"
            element={<ExponentsLesson />}
          />
          <Route path="/exams/gre/concepts/sets" element={<SetsLesson />} />
          <Route
            path="/exams/gre/concepts/functions"
            element={<FunctionsLesson />}
          />
          <Route
            path="/exams/gre/concepts/sequences"
            element={<SequencesLesson />}
          />
          <Route path="/exams/gre/concepts/saved" element={<SavedConcepts />} />
          <Route
            path="/exams/gre/verbal/vocabulary-book"
            element={<GREVocabularyLesson />}
          />
          <Route
            path="/exams/gre/strategy"
            element={<QuantStrategy />}
          />
          <Route
            path="/exams/gre/strategy/time-management"
            element={<Navigate to="/exams/gre/strategy" replace />}
          />
          <Route
            path="/exams/gre/strategy/question-triage"
            element={<Navigate to="/exams/gre/strategy" replace />}
          />
          <Route
            path="/exams/gre/strategy/estimation-techniques"
            element={<Navigate to="/exams/gre/strategy" replace />}
          />
          <Route
            path="/exams/gre/strategy/question-type-playbook"
            element={<Navigate to="/exams/gre/strategy" replace />}
          />
          <Route
            path="/exams/gre/strategy/problem-solving-framework"
            element={<Navigate to="/exams/gre/strategy" replace />}
          />
          <Route
            path="/exams/gre/strategy/calculator-strategies"
            element={<Navigate to="/exams/gre/strategy" replace />}
          />
          <Route
            path="/exams/gre/strategy/section-adaptive-scoring"
            element={<Navigate to="/exams/gre/strategy" replace />}
          />
          <Route path="/exams/gre/quant/progress" element={<QuantProgress />} />
          <Route
            path="/exams/gre/quant-practice/session"
            element={
              <PremiumRoute>
                <QuantPracticeSession />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/gre/awa/:testId"
            element={
              <PremiumRoute>
                <GREAWATest />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/gre/quant/:testId"
            element={
              <PremiumRoute>
                <GREQuantTest />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/gre/verbal/:testId"
            element={
              <PremiumRoute>
                <GREVerbalTest />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/gre/mock/:testId"
            element={
              <PremiumRoute>
                <GREAWATest />
              </PremiumRoute>
            }
          />
          <Route
            path="/exams/gre/full/:testId"
            element={
              <PremiumRoute>
                <GREFullTest />
              </PremiumRoute>
            }
          />
          <Route path="*" element={<DefaultRoute />} />
        </Routes>
      </Suspense>
      </LearnerAppSessionGuard>
    </>
  );
};

const DefaultRoute = () => {
  const user = getStoredUser();
  const hasValidSession = Boolean(user) && isTokenValid();

  if (hasValidSession && isLearnerAppBlockedUser(user)) {
    return <StaffOnlyAppRedirect />;
  }

  const landingPath = hasValidSession ? resolveAuthenticatedLandingPath(user) : '/login';

  return <Navigate to={landingPath || '/login'} replace />;
};

const App = () => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router>
        <AppShell />
      </Router>
    </GoogleOAuthProvider>
  );
};

export default App;
